import { lazy, Suspense, useMemo, useState } from "react";
import { useQuery, useZero } from "@rocicorp/zero/react";
// experiment/solar-icons — icons flow through the shared Solar-backed
// icons module (apps/desktop/src/components/ui/icons.tsx). Each export
// wraps a Solar icon with weight="Linear" defaulted.
import {
  SignOut,
  PencilSimple,
  User,
  Gear,
  Bell,
  Plug,
  MonitorSmartphone,
} from "@/components/ui/icons";
import { IconTooltip } from "@/components/ui/icon-tooltip";
import { Button } from "@/components/ui/button";
import { ToolbarIconButton } from "@/components/ui/toolbar-icon-button";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { AnimatedImage } from "@/components/ui/animated-image";
import { useCurrentUser } from "@/lib/current-user";
import { queries } from "@/lib/queries";
import { isSoundEnabled, setSoundEnabled } from "@/lib/sounds";
import { mutators } from "@/lib/mutators";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const SetStatusDialog = lazy(() =>
  import("@/components/dialogs/SetStatusDialog").then((m) => ({
    default: m.SetStatusDialog,
  })),
);
const ProfileDialog = lazy(() =>
  import("../dialogs/ProfileDialog").then((m) => ({
    default: m.ProfileDialog,
  })),
);
const GetTheAppsDialog = lazy(() =>
  import("@/components/dialogs/GetTheAppsDialog").then((m) => ({
    default: m.GetTheAppsDialog,
  })),
);
const NotificationSettingsDialog = lazy(() =>
  import("@/components/settings/NotificationSettingsDialog").then((m) => ({
    default: m.NotificationSettingsDialog,
  })),
);

interface UserProfileFooterProps {
  workspaceId?: string;
  isGuest?: boolean;
  onSignUp?: () => void;
}

export function UserProfileFooter({
  workspaceId,
  isGuest,
}: UserProfileFooterProps) {
  const { user, logout } = useCurrentUser();
  const zero = useZero();

  const [userRow] = useQuery(user ? queries.users.byId(user.id) : null);
  const [presenceRow] = useQuery(
    user ? queries.user_presence.byUserId(user.id) : null,
  );

  const [showSetStatus, setShowSetStatus] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showGetApps, setShowGetApps] = useState(false);
  const [showNotificationSettings, setShowNotificationSettings] =
    useState(false);
  const [soundEnabled, setSoundEnabledState] = useState(isSoundEnabled);

  const hasPendingDialog = useMemo(
    () =>
      showSetStatus || showProfile || showGetApps || showNotificationSettings,
    [showSetStatus, showProfile, showGetApps, showNotificationSettings],
  );

  if (!user) return null;

  // Singular Zero queries (not the users.all materialized selector) so
  // profile edits from ProfileDialog reflect immediately after Save.
  const liveDisplayName = userRow?.display_name || user.display_name;
  const avatarUrl = userRow?.avatar_url;
  const statusText = presenceRow?.status || "Online";
  const statusEmoji = presenceRow?.status_emoji;
  const statusExpiry = presenceRow?.status_expiry;

  return (
    <>
      <div className="px-3 py-[18px] relative shrink-0">
        <Separator className="absolute top-0 inset-x-0" />
        <div className="flex min-w-0 items-center gap-2">
          <div className="relative shrink-0">
            <div className="h-8 w-8 rounded-full border-0 bg-foreground flex items-center justify-center text-xs font-semibold overflow-hidden">
              {avatarUrl ? (
                <AnimatedImage
                  src={avatarUrl}
                  alt={liveDisplayName}
                  className="h-full w-full object-cover"
                />
              ) : (
                liveDisplayName.slice(0, 2).toUpperCase() || "??"
              )}
            </div>
            {!isGuest && (
              <div className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-success" />
            )}
          </div>
          {isGuest ? (
            <Button
              type="button"
              variant="ghost"
              onClick={() => setShowProfile(true)}
              className="h-auto min-h-8 min-w-0 flex-1 flex-col items-start justify-start self-stretch overflow-hidden px-1.5 py-0 text-left"
            >
              <p className="w-full truncate text-left text-sm font-medium">
                {liveDisplayName || "Guest"}
              </p>
              <p className="w-full truncate text-left text-[10px] text-muted-foreground">
                Guest
              </p>
            </Button>
          ) : (
            <>
              <div className="min-w-0 flex-1 overflow-hidden">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      className="h-auto min-h-8 w-full max-w-full min-w-0 flex-col items-start justify-start self-stretch overflow-hidden px-1.5 py-0 text-left"
                    >
                      <p className="w-full truncate text-left text-sm font-medium">
                        {liveDisplayName || "Unknown"}
                      </p>
                      <p className="flex w-full min-w-0 items-center gap-1 text-left text-[10px] text-muted-foreground">
                        {statusEmoji && (
                          <span className="shrink-0 text-sm">
                            {statusEmoji}
                          </span>
                        )}
                        <span className="min-w-0 flex-1 truncate text-left">
                          {statusText}
                        </span>
                      </p>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="min-w-[14rem]">
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger className="cursor-pointer">
                        {(() => {
                          return (
                            <span className="flex items-center gap-1.5">
                              Availability
                              <div
                                className={cn(
                                  "h-2 w-2 rounded-full",
                                  statusText === "Online" && "bg-success",
                                  statusText === "Away" && "bg-warning",
                                  statusText === "Busy" && "bg-destructive",
                                  statusText === "Do not disturb" &&
                                    "bg-destructive",
                                )}
                              />
                            </span>
                          );
                        })()}
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent className="min-w-[10rem]">
                        {["Online", "Away", "Busy", "Do not disturb"].map(
                          (s) => (
                            <DropdownMenuItem
                              key={s}
                              onClick={() => {
                                // Two presence mutations: status text + online flag.
                                // setStatus only touches `status` (omitted emoji
                                // and expiry stay unchanged via Zero update
                                // semantics). setOnline/setOffline updates
                                // is_online + last_active_at.
                                zero.mutate(
                                  mutators.user_presence.setStatus({
                                    user_id: user.id,
                                    status: s,
                                  }),
                                );
                                zero.mutate(
                                  s === "Online"
                                    ? mutators.user_presence.setOnline({
                                        user_id: user.id,
                                      })
                                    : mutators.user_presence.setOffline({
                                        user_id: user.id,
                                      }),
                                );
                              }}
                              className="cursor-pointer"
                            >
                              <div
                                className={cn(
                                  "h-2 w-2 rounded-full shrink-0",
                                  s === "Online" && "bg-success",
                                  s === "Away" && "bg-warning",
                                  s === "Busy" && "bg-destructive",
                                  s === "Do not disturb" && "bg-destructive",
                                )}
                              />
                              {s}
                            </DropdownMenuItem>
                          ),
                        )}
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>
                    <DropdownMenuItem
                      onClick={() => setShowSetStatus(true)}
                      className="cursor-pointer"
                    >
                      <PencilSimple
                        size={16}
                        weight="Linear"
                        className="shrink-0"
                      />
                      Update status
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onSelect={() => setShowProfile(true)}
                      className="cursor-pointer"
                    >
                      <User size={16} weight="Linear" className="shrink-0" />
                      Profile
                    </DropdownMenuItem>
                    {workspaceId && (
                      <DropdownMenuItem
                        onClick={() => setShowNotificationSettings(true)}
                        className="cursor-pointer"
                      >
                        <Gear size={16} weight="Linear" className="shrink-0" />
                        Preferences
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onSelect={(e) => {
                        e.preventDefault();
                        const next = !soundEnabled;
                        setSoundEnabled(next);
                        setSoundEnabledState(next);
                      }}
                      className="cursor-pointer"
                    >
                      <Bell size={16} weight="Linear" className="shrink-0" />
                      {soundEnabled ? "Notifications on" : "Notifications off"}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() =>
                        window.dispatchEvent(
                          new CustomEvent("ano:open-integrations"),
                        )
                      }
                      className="cursor-pointer"
                    >
                      <Plug size={16} weight="Linear" className="shrink-0" />
                      Integrations
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onSelect={() => setShowGetApps(true)}
                      className="cursor-pointer"
                    >
                      <MonitorSmartphone
                        size={16}
                        weight="Linear"
                        className="shrink-0"
                      />
                      Get the apps
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <IconTooltip label="Sign out">
                <ToolbarIconButton
                  onClick={logout}
                  className="h-8 w-8 shrink-0"
                  aria-label="Sign out"
                >
                  <SignOut
                    size={16}
                    weight="Linear"
                    className="text-muted-foreground"
                  />
                </ToolbarIconButton>
              </IconTooltip>
            </>
          )}
        </div>
      </div>

      {/* Dialogs owned by this component */}
      <Suspense
        fallback={
          hasPendingDialog ? (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-overlay/25"
              aria-hidden="true"
            >
              <div className="rounded border border-border bg-background px-3 py-2 text-xs text-muted-foreground">
                Loading dialog...
              </div>
            </div>
          ) : null
        }
      >
        <SetStatusDialog
          open={showSetStatus}
          onOpenChange={setShowSetStatus}
          userId={user.id}
          currentEmoji={statusEmoji}
          currentStatus={presenceRow?.status}
          currentExpiry={statusExpiry}
        />
        <ProfileDialog
          open={showProfile}
          onOpenChange={setShowProfile}
          userId={user.id}
          isGuest={isGuest}
        />
        <GetTheAppsDialog open={showGetApps} onOpenChange={setShowGetApps} />
        {workspaceId && (
          <NotificationSettingsDialog
            open={showNotificationSettings}
            onOpenChange={setShowNotificationSettings}
            userId={user.id}
            workspaceId={workspaceId}
          />
        )}
      </Suspense>
    </>
  );
}
